import { type CalendarRenderParameter, Empty, useCalendarDays } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import type { ActorEvent, FieldValue } from '@lighthouse/core'
import { type CalendarViewConfig, type RecordLikeProtocol } from '@lighthouse/core'
import {
    CenteredWrapper,
    getIsAppointField,
    getPrimaryDataSourceEnableFieldIds,
    getRecordPoolKey,
    getViewColumns,
    type MobileDrawerProps,
    NO_EDIT_SYSTEM_FIELD,
    notEditableFieldTypes,
    PRESET_PALETTES,
    useApplicationContext,
    useAtomData
} from '@lighthouse/shared'
import type { Vector2 } from '@use-gesture/react'
import { useDrag } from '@use-gesture/react'
import { eachDayOfInterval, getWeek, getYear, isAfter, isSameDay, parseISO, startOfDay } from 'date-fns'
import { produce } from 'immer'
import { merge } from 'rambda'
import React, { forwardRef, Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { throttle } from 'throttle-debounce'

import { useEffectiveAnchorId, useViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import * as SC from './CalendarBlock.style'
import { Cell, CELL_BORDER_BOTTOM_WIDTH, CELL_HEIGHT, DAY_MARGIN_BOTTOM, DAY_MARGIN_TOP, LINE_GAP, MINI_CELL_HEIGHT } from './Cell'
import { Header } from './Header'
import { TimeLine } from './TimeLine'
import { checkField, getSortedWeeks, getTimeLines } from './utils'

interface CalendarBlockProps {
    pageTarget?: MobileDrawerProps['target']
    onUpdateRecord: (recordId: string, content: RecordLikeProtocol['content']) => Promise<unknown>
    // onRecordAdd?: (initialValue?: Record<string, string | number>) => void
    handleClickAdd?: (content?: Record<string, FieldValue>) => Promise<RecordLikeProtocol | undefined>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
}

export const CalendarBlock = forwardRef<HTMLDivElement, CalendarBlockProps>((props, ref) => {
    const {
        pageTarget,

        onUpdateRecord: propOnUpdateRecord,
        // onRecordAdd,
        onRecordClick,
        handleClickAdd
    } = props

    const { isMobile } = useApplicationContext()

    const { blockData, dataSource, dataSourceList, recordPoints, recordPoolAtom } = useViewBlockContext()

    const { id, config, title } = blockData
    const {
        canCreateRecord,
        scheduleTitleField,
        scheduleStartDateField,
        scheduleEndDateField,
        scheduleColorMode = 'follow',
        scheduleColor = PRESET_PALETTES.purple,

        viewFieldSettings,
        scheduleColorField
    } = config as CalendarViewConfig
    const { breakPoint, anchor } = config
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)

    const { id: dsId, schema, viewOptions, type } = dataSource
    const { tableProps } = viewOptions || {}

    const records = useAtomData(
        recordPoolAtom,
        useCallback(
            pool => recordPoints.map(p => pool.get(getRecordPoolKey(dsId, p.id))).filter((r): r is RecordLikeProtocol => r !== undefined),
            [dsId, recordPoints]
        )
    )

    const primaryDataSourceFieldIds = useMemo(() => {
        if (!schema || !viewOptions || !type) {
            return
        }
        return getPrimaryDataSourceEnableFieldIds(schema, viewOptions, type)
    }, [schema, viewOptions, type])

    const configComplete = useMemo(
        () =>
            checkField(scheduleTitleField, schema) &&
            checkField(scheduleStartDateField, schema) &&
            checkField(scheduleEndDateField, schema),
        [scheduleEndDateField, scheduleStartDateField, scheduleTitleField, schema]
    )

    // 判断日期字段是否可拖拽编辑
    const editable = useMemo(() => {
        if (!scheduleStartDateField || !scheduleEndDateField || !schema) {
            return false
        }

        // 非主表字段
        if (!primaryDataSourceFieldIds?.has(scheduleStartDateField) || !primaryDataSourceFieldIds?.has(scheduleEndDateField)) {
            return false
        }

        const startField = schema[scheduleStartDateField]
        const endField = schema[scheduleEndDateField]

        if (!startField || !endField) {
            return false
        }

        // 检查字段类型是否不可编辑
        const isStartFieldEditable =
            !notEditableFieldTypes.has(startField.type) && !getIsAppointField(scheduleStartDateField, NO_EDIT_SYSTEM_FIELD)
        const isEndFieldEditable =
            !notEditableFieldTypes.has(endField.type) && !getIsAppointField(scheduleEndDateField, NO_EDIT_SYSTEM_FIELD)

        // 只有当开始和结束日期字段都可编辑时，才允许拖拽
        return isStartFieldEditable && isEndFieldEditable
    }, [scheduleStartDateField, scheduleEndDateField, schema, primaryDataSourceFieldIds])

    const canCreate = useMemo(() => !!canCreateRecord && editable, [canCreateRecord, editable])

    const tableRef = useRef<HTMLTableElement | null>(null)

    const [panelViewDate, setPanelViewDate] = useState(new Date())

    const hasInitializedPanelDate = useRef(false)

    // 日历打开时，不要傻乎乎地停在"今天"，而是智能跳转到离今天最近的那条有记录的日期对应的月份面板。
    useLayoutEffect(() => {
        if (hasInitializedPanelDate.current || !scheduleStartDateField || !scheduleEndDateField || records.length === 0) {
            return
        }
        hasInitializedPanelDate.current = true

        const today = startOfDay(new Date())
        const todayMs = today.getTime()

        let bestDate = today
        let bestDistance = Infinity
        let bestIsFuture = false

        for (const record of records) {
            const startValue = record.content[scheduleStartDateField]?.value
            const endValue = record.content[scheduleEndDateField]?.value

            if (typeof startValue !== 'number' || typeof endValue !== 'number' || startValue > endValue) {
                continue
            }

            const startMs = startOfDay(new Date(startValue)).getTime()
            const endMs = startOfDay(new Date(endValue)).getTime()

            let representativeDate: Date
            let distance: number
            let isFuture: boolean

            if (endMs < todayMs) {
                representativeDate = new Date(endMs)
                distance = todayMs - endMs
                isFuture = false
            } else if (startMs > todayMs) {
                representativeDate = new Date(startMs)
                distance = startMs - todayMs
                isFuture = true
            } else {
                representativeDate = today
                distance = 0
                isFuture = false
            }

            if (distance < bestDistance || (distance === bestDistance && isFuture && !bestIsFuture)) {
                bestDistance = distance
                bestDate = representativeDate
                bestIsFuture = isFuture
            }
        }

        setPanelViewDate(bestDate)
    }, [records, scheduleStartDateField, scheduleEndDateField])

    const [isSelecting, setIsSelecting] = useState(false)
    const [selectedDates, setSelectedDates] = useState<Date[]>([])

    const selectHandler = throttle(0, (initXy: Vector2, finalXy: Vector2) => {
        const tableEl = tableRef.current
        if (!tableEl) {
            return
        }

        const cells = tableEl.querySelectorAll('td > div')
        let firstCell: Element | null = null
        let lastCell: Element | null = null

        const [sx, sy, ex, ey] = initXy[1] < finalXy[1] ? [...initXy, ...finalXy] : [...finalXy, ...initXy]

        for (const el of cells) {
            const rect = el.getBoundingClientRect()
            if (rect.right > sx && rect.bottom > sy && !firstCell) {
                firstCell = el
            }
            if (rect.left < ex && rect.top < ey) {
                lastCell = el
            }
        }

        if (!lastCell || !firstCell) {
            return
        }

        const startDateValue = firstCell.getAttribute('data-value')
        const endDateValue = lastCell.getAttribute('data-value')
        if (!startDateValue || !endDateValue) {
            return
        }

        let startDate = parseISO(startDateValue)
        let endDate = parseISO(endDateValue)

        if (isAfter(startDate, endDate)) {
            ;[startDate, endDate] = [endDate, startDate]
        }

        const dates = eachDayOfInterval({ start: startDate, end: endDate })
        setSelectedDates(dates)
    })

    const clickHandler = useCallback(
        (e: MouseEvent) => {
            if (!canCreate) {
                return
            }
            const paths = e.composedPath()
            if (paths.some(el => el instanceof HTMLElement && el.hasAttribute('data-ignore-tap'))) {
                return
            }
            const cell = paths.find(el => el instanceof HTMLElement && el.hasAttribute('data-value')) as HTMLElement | undefined

            if (!cell) {
                return
            }

            const dateStr = cell.getAttribute('data-value')!

            const date = parseISO(dateStr)

            handleClickAdd?.(
                merge(
                    scheduleStartDateField
                        ? {
                              [scheduleStartDateField]: { value: date.valueOf() }
                          }
                        : {},
                    scheduleEndDateField
                        ? {
                              [scheduleEndDateField]: { value: date.valueOf() }
                          }
                        : {}
                )
            )
        },
        [canCreate, handleClickAdd, scheduleEndDateField, scheduleStartDateField]
    )

    // PC端时的框选及点击事件
    useDrag(
        state => {
            if (!canCreate) {
                return
            }
            if (state.tap) {
                clickHandler(state.event as MouseEvent)
                return
            }

            if (state.first) {
                setIsSelecting(true)
            }
            if (state.last) {
                setIsSelecting(false)

                if (selectedDates.length === 0) {
                    return
                }
                handleClickAdd?.(
                    merge(
                        scheduleStartDateField
                            ? {
                                  [scheduleStartDateField]: { value: selectedDates[0].valueOf() }
                              }
                            : {},
                        scheduleEndDateField
                            ? {
                                  [scheduleEndDateField]: { value: selectedDates[selectedDates.length - 1].valueOf() }
                              }
                            : {}
                    )
                )
                setSelectedDates([])
                return
            }

            selectHandler(state.initial, state.values)
        },
        {
            filterTaps: true,
            enabled: !isMobile && canCreate,
            target: tableRef
        }
    )

    const [draftRecord, setDraftRecord] = useState<RecordLikeProtocol | null>(null)
    const localRecords = useMemo(() => [...records, draftRecord].filter(Boolean) as RecordLikeProtocol[], [draftRecord, records])

    const onUpdateLocalRecord = useCallback(
        (id: string, startDate: Date, endDate: Date) => {
            if (!scheduleStartDateField || !scheduleEndDateField) {
                return
            }

            const record = records.find(item => item.id === id)
            if (!record) {
                return
            }

            setDraftRecord(
                produce(record, draft => {
                    draft.id = `draft-${draft.id}`
                    draft.content[scheduleStartDateField] = { value: startDate.valueOf() }
                    draft.content[scheduleEndDateField] = { value: endDate.valueOf() }
                })
            )
        },
        [records, scheduleEndDateField, scheduleStartDateField]
    )

    const onUpdateRecord = useCallback(
        async (id: string, startDate: Date, endDate: Date) => {
            if (!scheduleStartDateField || !scheduleEndDateField) {
                return
            }

            await propOnUpdateRecord?.(id, {
                [scheduleStartDateField]: { value: startDate.valueOf() },
                [scheduleEndDateField]: { value: endDate.valueOf() }
            })
        },
        [propOnUpdateRecord, scheduleEndDateField, scheduleStartDateField]
    )

    // // 移动端时的点击事件
    // useLayoutEffect(() => {
    //     if (isMobile) {
    //         const table = tableRef.current
    //         if (!table) {
    //             return
    //         }

    //         table.addEventListener('click', clickHandler)

    //         return () => table.removeEventListener('click', clickHandler)
    //     }
    // }, [clickHandler, isMobile])

    const [hoveredLine, setHoveredLine] = useState('')

    const settingColumns = useMemo(
        () =>
            getViewColumns({
                blockId: id,
                tableProps,
                value: viewFieldSettings,
                schema
            }),
        [id, schema, tableProps, viewFieldSettings]
    )

    const columns = useMemo(() => settingColumns.filter(item => item.visible), [settingColumns])

    const timeLines = useMemo(
        () =>
            getTimeLines(localRecords, {
                dataSource,
                columns: settingColumns,
                titleField: scheduleTitleField,
                startField: scheduleStartDateField,
                endField: scheduleEndDateField,
                colorMode: scheduleColorMode,
                color: scheduleColor,
                scheduleColorField
            }),
        [
            localRecords,
            dataSource,
            settingColumns,
            scheduleTitleField,
            scheduleStartDateField,
            scheduleEndDateField,
            scheduleColorMode,
            scheduleColor,
            scheduleColorField
        ]
    )

    const calendarDays = useCalendarDays({ calendarDate: panelViewDate, firstDayOfWeek: 0 }).flat()

    const weeks = useMemo(() => getSortedWeeks(timeLines, calendarDays), [calendarDays, timeLines])

    const [resizingId, setResizingId] = useState('')
    const [draggingId, setDraggingId] = useState('')

    const [maxHeightOfIndexMap, setMaxHeightOfIndexMap] = useState<Record<string, number[]>>({})

    // 修正最大高度
    useEffect(() => {
        setMaxHeightOfIndexMap(s =>
            produce(s, draft => {
                Object.keys(draft).forEach(week => {
                    if (weeks.some(item => item[0] === week)) {
                        return
                    }

                    delete draft[week]
                })
                weeks.forEach(([week, lines]) => {
                    const maxIndex = Math.max(...lines.map(line => line.index))
                    if (!draft[week]) {
                        return
                    }
                    if (draft[week].length - 1 > maxIndex) {
                        draft[week].splice(maxIndex + 1)
                    }
                })
            })
        )
    }, [weeks])

    const _Cell = useMemo(() => {
        return ({ date, ...args }: CalendarRenderParameter) => {
            if (!date) {
                return null
            }

            const week = `${getYear(date)}-${getWeek(date, { weekStartsOn: 0 })}`

            const height = maxHeightOfIndexMap[week]?.reduce(
                (prev, next, index) => prev + next + (index === 0 ? 0 : LINE_GAP),
                (isMobile ? MINI_CELL_HEIGHT : CELL_HEIGHT) + CELL_BORDER_BOTTOM_WIDTH + DAY_MARGIN_TOP + DAY_MARGIN_BOTTOM
            )

            return (
                <Cell
                    {...args}
                    style={{ height: height === 0 ? undefined : height }}
                    target={pageTarget}
                    date={date}
                    data={timeLines}
                    selected={selectedDates.some(item => isSameDay(item, date))}
                    isSelecting={isSelecting}
                    isMobile={isMobile}
                    canCreate={canCreate}
                    onClickSchedule={onRecordClick}
                />
            )
        }
    }, [maxHeightOfIndexMap, isMobile, pageTarget, timeLines, selectedDates, isSelecting, canCreate, onRecordClick])

    const _Header = useMemo(() => {
        return (props: CalendarRenderParameter) => <Header isMobile={isMobile} {...props} />
    }, [isMobile])

    return (
        <SC.Container
            ref={ref}
            blockId={id}
            blockName={title}
            animation={breakPoint.animation}
            id={effectiveAnchorId}
            style={{ userSelect: draggingId ? 'none' : undefined }}
        >
            {configComplete ? (
                <SC.StyledCalendar
                    ref={r => {
                        tableRef.current = r?.querySelector('table') || null
                    }}
                    weekFormat={isMobile ? 'iiiiii' : 'iii'}
                    firstDayOfWeek={0}
                    panelDate={panelViewDate}
                    onPanelDateChange={setPanelViewDate}
                    HeaderComponent={_Header}
                    FooterComponent={() => null}
                    CellComponent={_Cell}
                />
            ) : (
                <CenteredWrapper style={{ padding: 36 }}>
                    <Empty
                        icon={
                            <img
                                style={{ width: 140, height: 144 }}
                                src={getAssetUrl('empty', 'no_calendar_schedule.svg')}
                                alt="请在「右侧栏>配置」中选择日程标题及日期字段"
                            />
                        }
                        description="请在「右侧栏>配置」中选择日程标题及日期字段"
                    />
                </CenteredWrapper>
            )}

            {weeks.map(([week, item]) => {
                return (
                    <Fragment key={week}>
                        {item.map(line => {
                            const prevHeight = maxHeightOfIndexMap[week]?.reduce(
                                (prev, next, index) => (index < line.index ? prev + next : prev),
                                0
                            )

                            return (
                                <TimeLine
                                    key={line.id}
                                    style={{
                                        minHeight: maxHeightOfIndexMap[week]?.[line.index],
                                        pointerEvents: !!resizingId || !!draggingId || isSelecting ? 'none' : undefined
                                    }}
                                    record={line.record}
                                    schema={schema}
                                    columns={columns}
                                    prevHeight={prevHeight}
                                    onHeightChange={v =>
                                        setMaxHeightOfIndexMap(s =>
                                            produce(s, draft => {
                                                if (!draft[week]) {
                                                    draft[week] = []
                                                }
                                                draft[week][line.index] = v
                                            })
                                        )
                                    }
                                    info={line}
                                    isMobile={isMobile}
                                    tableElement={tableRef}
                                    editable={editable}
                                    onUpdateDate={onUpdateRecord}
                                    onUpdateLocalDate={onUpdateLocalRecord}
                                    onClickSchedule={onRecordClick}
                                    hoveredLine={isMobile ? '' : hoveredLine}
                                    onResizeStart={() => setResizingId(line.id)}
                                    onResizeEnd={() => {
                                        setResizingId('')
                                        setDraftRecord(null)
                                    }}
                                    onDragStart={() => {
                                        setDraggingId(line.id)
                                    }}
                                    onDragEnd={() => {
                                        setDraggingId('')
                                        setDraftRecord(null)
                                    }}
                                    onMouseOver={() => setHoveredLine(line.id)}
                                    onMouseOut={() => setHoveredLine('')}
                                />
                            )
                        })}
                    </Fragment>
                )
            })}
        </SC.Container>
    )
})
